import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2 } from 'lucide-react'
import { FieldErrors, FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'

import { Button } from '@/components/ui/button'
import { Participant } from '@/interfaces/Participant'
import API from '@/lib/api'
import { components } from '@invo.space/shared'

import { ParticipantFormFull } from './ParticipantFormFull'
import { ParticipantFormSchema } from './schema'

const schema = z.object({
  customer: ParticipantFormSchema,
  beneficiary: ParticipantFormSchema,
  supplier: ParticipantFormSchema,
})

export type ParticipantFormValues = z.infer<typeof schema>

export type StandaloneParticipantFormProps = {
  id?: Participant['id']
  defaultValues?: Partial<ParticipantFormValues>
  type: 'customer' | 'beneficiary' | 'supplier'
  onSuccess?: (data: components['schemas']['Participant']) => void
  showAccountNumber?: boolean
  showDefaultBankAccount?: boolean
  /** Text tlačítka pro uložení (výchozí "Uložit") */
  submitLabel?: string
  /** Zobrazí tlačítko Zrušit vedle uložení */
  onCancel?: () => void
}

export const StandaloneParticipantForm = ({
  id,
  defaultValues,
  type,
  onSuccess,
  showAccountNumber = false,
  showDefaultBankAccount = false,
  submitLabel,
  onCancel,
}: StandaloneParticipantFormProps) => {
  const { t } = useTranslation()
  const methods = useForm<ParticipantFormValues>({
    resolver: zodResolver(schema.pick({ [type]: true } as any)),
    defaultValues,
  })

  const toPayload = (values: ParticipantFormValues) => ({
    ...values[type],
    ico: values[type].businessId,
    dic: values[type].taxId,
    businessId: undefined,
    taxId: undefined,
  })

  const onValid = async (values: ParticipantFormValues) => {
    try {
      const { data } = id
        ? await API.patch<components['schemas']['Participant']>(`/api/v2/contacts/${id}`, toPayload(values))
        : await API.post<components['schemas']['Participant']>(
            `/api/v2/contacts`,
            toPayload(values) as components['schemas']['Participant'],
          )
      onSuccess?.(data)
    } catch (err) {
      const response = (err as { response?: { status?: number; data?: { type?: string } } }).response
      const notFound = response?.status === 404 && response?.data?.type === 'not-found-in-external-service'
      toast.error(id ? 'Kontakt se nepodařilo uložit.' : t('participant.new.error.title'), {
        description: notFound ? t('participant.new.error.not-found') : id ? 'Zkuste to prosím znovu.' : t('participant.new.error.description'),
        duration: 5000,
        dismissible: true,
        closeButton: true,
      })
    }
  }

  const onInvalid = (errors: FieldErrors<ParticipantFormValues>) => {
    console.log('Form errors', errors, methods.getValues())
  }

  const { isSubmitting } = methods.formState

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onValid, onInvalid)} className="w-full max-w-2xl">
        <ParticipantFormFull
          prefix={type}
          showAccountNumber={showAccountNumber}
          showDefaultBankAccount={showDefaultBankAccount}
        />

        <div className="mt-8 flex items-center gap-2 border-t pt-5">
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="animate-spin" />}
            {submitLabel ?? t('generic.button.save')}
          </Button>
          {onCancel && (
            <Button type="button" variant="ghost" onClick={onCancel}>
              Zrušit
            </Button>
          )}
        </div>
      </form>
    </FormProvider>
  )
}
